import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'

export const IMDB_ORG_CLAIM_KEY = 'https://inflectionmedicine.com/org_id'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * True when `value` is a non-empty string matching a standard RFC-style UUID hex pattern.
 * Shared primitive for IMDB org ids, patient/family-member ids, and other UUID-shaped strings.
 * (No org-specific rules — use {@link isImdbOrgUuid} only when the value is meant to be an organization id.)
 */
export function isStandardUuidString(value: string | null | undefined): boolean {
  return Boolean(value && UUID_RE.test(String(value).trim()))
}

/**
 * True when `value` looks like an IMDB **organization** id string for ordering/database APIs
 * (today: same as {@link isStandardUuidString}; Epic numeric client keys like `11112` are rejected).
 * If org-specific validation is added later, it belongs here only — not in `isStandardUuidString`.
 */
export function isImdbOrgUuid(value: string | null | undefined): boolean {
  return isStandardUuidString(value)
}

/** IMDB org UUID from Auth0-shaped / epic-provision JWT (must match ordering API body). */
export function tryImdbOrgIdFromBearerJwt(token: string | null | undefined): string | null {
  if (!token?.includes('.')) {
    return null
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as Record<string, unknown>
    const raw = payload[IMDB_ORG_CLAIM_KEY]
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null
  } catch {
    return null
  }
}

/**
 * Resolve organizationId for POST/PATCH ordering APIs. Must be the IMDB UUID that matches the JWT
 * (never Epic numeric ids like "11112").
 */
export async function resolveImdbOrgIdForOrderingApi(params: {
  orgIdFromAuth: string | null | undefined
  /** Only honored if already a UUID (e.g. standalone form) — Epic numeric ids are ignored. */
  additionalUuidCandidate?: string | null | undefined
  isEpicFlow: boolean
  epicOAuthState: string | null | undefined
}): Promise<string> {
  const { orgIdFromAuth, additionalUuidCandidate, isEpicFlow, epicOAuthState } = params
  const primary = (orgIdFromAuth || '').trim()
  if (isImdbOrgUuid(primary)) {
    return primary
  }
  if (isEpicFlow && epicOAuthState) {
    try {
      const epic = EpicAuthClient.getInstance()
      const provisioned = await epic.getOrRefreshProviderApiToken(epicOAuthState)
      const fromJwt = tryImdbOrgIdFromBearerJwt(provisioned)
      if (fromJwt && isImdbOrgUuid(fromJwt)) {
        return fromJwt.trim()
      }
    } catch {
      // fall through
    }
  }
  const extra = (additionalUuidCandidate || '').trim()
  if (isImdbOrgUuid(extra)) {
    return extra
  }
  return ''
}
